// Application Configuration with Environment Support
// ***** Code Start BH29072026_1105 - Merge Sathis env-driven endpoints; keep prod fail-closed / dev proxy
import {
  DEV_LLM_API_URL,
  defaultLlmApiBaseUrl,
  defaultLlmApiBaseUrlAbsolute,
  defaultFrontendOrigin,
  defaultSupportEmail,
  resolveAppEnvironment,
  canonicalSpaOrigin,
  sameSpaSite,
  legacyApiPath,
} from './environments';
import { resolveRecaptchaSiteKey } from './recaptchaConfig';
import {
  catalogMonthlyPrice,
  catalogAddonPriceByTokens,
} from './paddlePriceCatalog';

const appEnvironment = resolveAppEnvironment();
const isDev = appEnvironment === 'development';
const isQa = appEnvironment === 'qa';
const isProd = appEnvironment === 'production';
const isDevServer = appEnvironment === 'dev_server';
// Dev Server SPA builds against candordev — use the same pri_* map as Prod (not empty fail-closed).
const useCatalogPriceFallback = isDev || isDevServer || isQa || isProd;

// ***** Code Start BH15092026_1445 - Production FRONTEND_ORIGIN pins packaged SPA (not cantest)
function resolveFrontendOrigin() {
  const fromEnv = (process.env.REACT_APP_FRONTEND_ORIGIN || '').trim();
  const packaged = fromEnv || defaultFrontendOrigin() || canonicalSpaOrigin();
  if (typeof window === 'undefined') return packaged;
  if (isProd) {
    // Production package always uses candorsys (www/apex), never the live page host if wrong.
    return packaged || window.location.origin;
  }
  if (fromEnv && sameSpaSite(fromEnv, window.location.origin)) return fromEnv.replace(/\/$/, '');
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  return window.location.origin || packaged;
}

export const FRONTEND_ORIGIN = resolveFrontendOrigin();
// ***** Code End BH15092026_1445

// BrowserRouter-aware URL builder for Paddle redirect URLs (formerly HashRouter-aware)
export const withHashRoute = (path) => {
  const prefix = FRONTEND_ORIGIN.endsWith('/') ? FRONTEND_ORIGIN.slice(0, -1) : FRONTEND_ORIGIN;
  return `${prefix}${path.startsWith('/') ? path : `/${path}`}`;
};

// Helper to sanitize placeholder values from environment templates
const cleanPlaceholder = (val, fallback = '') => {
  if (
    !val ||
    val === 'YOUR_LIVE_CLIENT_KEY_HERE' ||
    val === 'YOUR_LIVE_PRICE_PRO_HERE' ||
    val === 'YOUR_LIVE_PRICE_PAY_AS_YOU_GO_HERE' ||
    String(val).startsWith('YOUR_') ||
    String(val).includes('_HERE')
  ) {
    return fallback;
  }
  return val;
};

/** Resolve API path from env, else matrix legacy_api_paths / explicit fallback key. */
export const resolveEndpoint = (envKey, matrixKeyOrFallback) => {
  const fromEnv = envKey ? process.env[envKey] : null;
  if (fromEnv && String(fromEnv).trim()) return String(fromEnv).trim();
  if (typeof matrixKeyOrFallback === 'string' && matrixKeyOrFallback.startsWith('/')) {
    return matrixKeyOrFallback;
  }
  return legacyApiPath(matrixKeyOrFallback);
};

const config = {
  // Application Information
  app: {
    name: process.env.REACT_APP_NAME || 'Candor SI',
    version: process.env.REACT_APP_VERSION || '1.0.0',
    environment: process.env.REACT_APP_ENVIRONMENT || appEnvironment,
    title: process.env.REACT_APP_DASHBOARD_TITLE || 'Subscription Management',
    description: process.env.REACT_APP_DASHBOARD_DESCRIPTION || 'Manage your Candor SI subscription and billing',
    supportEmail: process.env.REACT_APP_SUPPORT_EMAIL || defaultSupportEmail(),
    supportSiteUrl: process.env.REACT_APP_SUPPORT_SITE_URL || defaultFrontendOrigin(),
    dashboardUrl: process.env.REACT_APP_DASHBOARD_URL || defaultFrontendOrigin(),
  },

  // API Configuration — DEV proxy / QA / PROD from host matrix + REACT_APP_*
  api: {
    baseUrl:
      process.env.REACT_APP_API_BASE_URL !== undefined && process.env.REACT_APP_API_BASE_URL !== ''
        ? String(process.env.REACT_APP_API_BASE_URL).replace(/\/$/, '')
        : defaultLlmApiBaseUrl(),
    proxyTarget: process.env.REACT_APP_API_PROXY_TARGET || DEV_LLM_API_URL,
    version: process.env.REACT_APP_API_VERSION || 'v1',
    timeout: parseInt(process.env.REACT_APP_API_TIMEOUT, 10) || (isDev ? 30000 : 60000),
    retryAttempts: parseInt(process.env.REACT_APP_API_RETRY_ATTEMPTS, 10) || (isDev ? 3 : 5),
    llmApiHost: defaultLlmApiBaseUrlAbsolute(),
    endpoints: {
      auth: resolveEndpoint('REACT_APP_ENDPOINT_AUTH_TOKEN', 'token'),
      users: resolveEndpoint('REACT_APP_ENDPOINT_USER_PROFILE', '/api/v1/users/profile'),
      payments: resolveEndpoint('REACT_APP_ENDPOINT_PAYMENT_PLANS', '/api/v1/payments/plans'),
      subscriptions: resolveEndpoint('REACT_APP_ENDPOINT_SUBSCRIPTION_STATUS', '/api/v1/subscriptions/status'),
      webhooks: '/api/v1/payments/webhook',
      cdp: {
        ide: resolveEndpoint('REACT_APP_ENDPOINT_CDP_IDE', '/api/v1/cdp/ide'),
        projects: resolveEndpoint('REACT_APP_ENDPOINT_CDP_PROJECTS', '/api/v1/cdp/projects'),
        usage: resolveEndpoint('REACT_APP_ENDPOINT_SUBSCRIPTION_USAGE_LIMIT', '/api/v1/subscriptions/usage-limit'),
        sync: resolveEndpoint('REACT_APP_ENDPOINT_CDP_SYNC', '/api/v1/cdp/sync'),
        status: resolveEndpoint('REACT_APP_ENDPOINT_SUBSCRIPTION_STATUS', '/api/v1/subscriptions/status'),
        notifications: resolveEndpoint('REACT_APP_ENDPOINT_CDP_NOTIFICATIONS', '/api/v1/cdp/notifications'),
        plans: resolveEndpoint('REACT_APP_ENDPOINT_SUBSCRIPTION_PLANS', '/api/v1/subscriptions/plans'),
        upgrade: resolveEndpoint('REACT_APP_ENDPOINT_SUBSCRIPTION_UPGRADE', '/api/v1/subscriptions/upgrade'),
        cancel: resolveEndpoint('REACT_APP_ENDPOINT_SUBSCRIPTION_CANCEL', '/api/v1/payments/cancel-subscription'),
        billing: resolveEndpoint('REACT_APP_ENDPOINT_PAYMENT_BILLING_HISTORY', '/api/v1/payments/billing-history'),
      },
      // Full paths for fetch() callers (LoginModal, DownloadModal, etc.)
      raw: {
        AUTH_REGISTER: resolveEndpoint('REACT_APP_ENDPOINT_AUTH_REGISTER', 'register'),
        AUTH_VERIFY_OTP: resolveEndpoint('REACT_APP_ENDPOINT_AUTH_VERIFY_OTP', 'verify_otp'),
        AUTH_RESEND_OTP: resolveEndpoint('REACT_APP_ENDPOINT_AUTH_RESEND_OTP', 'resend_otp'),
        AUTH_FORGOT_PASSWORD: resolveEndpoint('REACT_APP_ENDPOINT_AUTH_FORGOT_PASSWORD', 'forgot_password'),
        AUTH_RESET_PASSWORD: resolveEndpoint('REACT_APP_ENDPOINT_AUTH_RESET_PASSWORD', 'reset_password'),
        AUTH_CONTACT_US: resolveEndpoint('REACT_APP_ENDPOINT_AUTH_CONTACT_US', 'contact_us'),
        AUTH_LATEST_DOWNLOAD: resolveEndpoint('REACT_APP_ENDPOINT_AUTH_LATEST_DOWNLOAD', 'latest_download'),
        AUTH_EXCHANGE_SESSION: resolveEndpoint('REACT_APP_ENDPOINT_AUTH_EXCHANGE_SESSION', 'exchange_session'),
        USER_PROFILE: resolveEndpoint('REACT_APP_ENDPOINT_USER_PROFILE', '/api/v1/users/profile'),
        USER_DEBUG_PROFILE: resolveEndpoint('REACT_APP_ENDPOINT_USER_DEBUG_PROFILE', '/api/v1/users/debug/user-profile'),
        SUBSCRIPTION_STATUS: resolveEndpoint('REACT_APP_ENDPOINT_SUBSCRIPTION_STATUS', '/api/v1/subscriptions/status'),
        SUBSCRIPTION_UPDATE: resolveEndpoint('REACT_APP_ENDPOINT_SUBSCRIPTION_UPDATE', '/api/v1/subscriptions/update'),
        SUBSCRIPTION_PLANS: resolveEndpoint('REACT_APP_ENDPOINT_SUBSCRIPTION_PLANS', '/api/v1/subscriptions/plans'),
        SUBSCRIPTION_ADDONS: resolveEndpoint('REACT_APP_ENDPOINT_SUBSCRIPTION_ADDONS', '/api/v1/subscriptions/plans/addons'),
        SUBSCRIPTION_UPGRADE: resolveEndpoint('REACT_APP_ENDPOINT_SUBSCRIPTION_UPGRADE', '/api/v1/subscriptions/upgrade'),
        SUBSCRIPTION_CANCEL: resolveEndpoint('REACT_APP_ENDPOINT_SUBSCRIPTION_CANCEL', '/api/v1/payments/cancel-subscription'),
        SUBSCRIPTION_USAGE_LIMIT: resolveEndpoint('REACT_APP_ENDPOINT_SUBSCRIPTION_USAGE_LIMIT', '/api/v1/subscriptions/usage-limit'),
        PAYMENT_PLANS: resolveEndpoint('REACT_APP_ENDPOINT_PAYMENT_PLANS', '/api/v1/payments/plans'),
        PAYMENT_CREATE_CHECKOUT: resolveEndpoint('REACT_APP_ENDPOINT_PAYMENT_CREATE_CHECKOUT', '/api/v1/payments/create-checkout-session'),
        PAYMENT_VERIFY_CHECKOUT: resolveEndpoint('REACT_APP_ENDPOINT_PAYMENT_VERIFY_CHECKOUT', '/api/v1/payments/verify-checkout-session'),
        PAYMENT_VERIFY_TRANSACTION: resolveEndpoint('REACT_APP_ENDPOINT_PAYMENT_VERIFY_TRANSACTION', '/api/v1/payments/verify-transaction'),
        PAYMENT_BILLING_HISTORY: resolveEndpoint('REACT_APP_ENDPOINT_PAYMENT_BILLING_HISTORY', '/api/v1/payments/billing-history'),
        PAYMENT_PADDLE_INVOICES: resolveEndpoint('REACT_APP_ENDPOINT_PAYMENT_PADDLE_INVOICES', '/api/v1/payments/paddle/invoices'),
        DOWNLOAD_TRACK: resolveEndpoint('REACT_APP_ENDPOINT_DOWNLOAD_TRACK', '/api/v1/downloads/track'),
      },
    },
  },

  // ***** Code Start BH28072026_1045 / BH10092026_1430 - Shared pri_* catalog for Dev Server + Prod
  paddle: {
    clientKey: cleanPlaceholder(
      process.env.REACT_APP_PADDLE_CLIENT_KEY,
      isProd || isQa || isDevServer ? '' : 'test_1027fdb5f8d30d258d4d1586172'
    ),
    environment:
      process.env.REACT_APP_PADDLE_ENVIRONMENT ||
      (isDev || isDevServer ? 'sandbox' : 'production'),
    pricePro: cleanPlaceholder(
      process.env.REACT_APP_PADDLE_PRICE_PRO,
      useCatalogPriceFallback ? catalogMonthlyPrice('pro_plan') : ''
    ),
    priceDevPlus: cleanPlaceholder(
      process.env.REACT_APP_PADDLE_PRICE_DEV_PLUS || process.env.REACT_APP_PADDLE_PRICE_DEVELOPER_PLUS,
      useCatalogPriceFallback ? catalogMonthlyPrice('developer_plus') : ''
    ),
    priceDevUltimate: cleanPlaceholder(
      process.env.REACT_APP_PADDLE_PRICE_DEV_ULTIMATE || process.env.REACT_APP_PADDLE_PRICE_DEVELOPER_ULTIMATE,
      useCatalogPriceFallback ? catalogMonthlyPrice('developer_ultimate') : ''
    ),
    pricePayAsYouGo: cleanPlaceholder(
      process.env.REACT_APP_PADDLE_PRICE_PAY_AS_YOU_GO,
      useCatalogPriceFallback ? catalogMonthlyPrice('pay_as_you_go') : ''
    ),
    priceAddon1M: cleanPlaceholder(
      process.env.REACT_APP_PADDLE_PRICE_ADDON_1M,
      useCatalogPriceFallback ? catalogAddonPriceByTokens(1000000) : ''
    ),
    priceAddon2M: cleanPlaceholder(
      process.env.REACT_APP_PADDLE_PRICE_ADDON_2M,
      useCatalogPriceFallback ? catalogAddonPriceByTokens(2000000) : ''
    ),
    priceAddon3M: cleanPlaceholder(
      process.env.REACT_APP_PADDLE_PRICE_ADDON_3M,
      useCatalogPriceFallback ? catalogAddonPriceByTokens(3000000) : ''
    ),
  // ***** Code End BH28072026_1045 / BH10092026_1430
    currency: 'usd',
    locale: 'en',
    buildSuccessUrl(planId, billingCycle = 'monthly') {
      return withHashRoute(`/success?success=true&plan=${encodeURIComponent(planId)}&billing_cycle=${encodeURIComponent(billingCycle)}&transaction_id={TRANSACTION_ID}`);
    },
    buildCancelUrl(planId, billingCycle = 'monthly') {
      return withHashRoute(`/success?success=false&plan=${encodeURIComponent(planId)}&billing_cycle=${encodeURIComponent(billingCycle)}`);
    },
  },

  // Stripe Configuration
  stripe: {
    publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
  },

  // reCAPTCHA Configuration
  recaptcha: {
    siteKey: resolveRecaptchaSiteKey(),
  },

  // CDP IDE Integration - Environment Based
  cdp: {
    ideName: process.env.REACT_APP_CDP_IDE_NAME || 'Candor Development Platform',
    ideVersion: process.env.REACT_APP_CDP_IDE_VERSION || '2.0.0',
    integrationEnabled: process.env.REACT_APP_CDP_INTEGRATION_ENABLED !== 'false',
    postMessageOrigin: process.env.REACT_APP_CDP_POST_MESSAGE_ORIGIN || (isDev ? '*' : defaultFrontendOrigin()),
    ideUrl: process.env.REACT_APP_CDP_IDE_URL || defaultFrontendOrigin(),
    dashboardUrl: process.env.REACT_APP_DASHBOARD_URL || defaultFrontendOrigin(),
    realTimeSync: process.env.REACT_APP_CDP_REALTIME_SYNC === 'true',
    autoRefresh: process.env.REACT_APP_CDP_AUTO_REFRESH !== 'false',
  },

  // Desktop App Integration (Legacy)
  desktop: {
    appName: process.env.REACT_APP_DESKTOP_APP_NAME || 'Candor SI Desktop',
    appVersion: process.env.REACT_APP_DESKTOP_APP_VERSION || '1.0.0',
    integrationEnabled: process.env.REACT_APP_DESKTOP_INTEGRATION_ENABLED === 'true',
    postMessageOrigin: process.env.REACT_APP_DESKTOP_POST_MESSAGE_ORIGIN || '*',
  },

  // Feature Flags - Environment Based
  features: {
    analytics: process.env.REACT_APP_FEATURE_ANALYTICS === 'true',
    billingHistory: process.env.REACT_APP_FEATURE_BILLING_HISTORY === 'true',
    paymentMethods: process.env.REACT_APP_FEATURE_PAYMENT_METHODS === 'true',
    usageTracking: process.env.REACT_APP_FEATURE_USAGE_TRACKING === 'true',
    trialCountdown: process.env.REACT_APP_FEATURE_TRIAL_COUNTDOWN === 'true',
    tokenUsage: process.env.REACT_APP_FEATURE_TOKEN_USAGE === 'true',
    enableBackgroundRefresh: process.env.REACT_APP_FEATURE_BACKGROUND_REFRESH !== 'false',
    allowMockPayments: process.env.REACT_APP_ALLOW_MOCK_PAYMENTS === 'true',
  },

  // UI Configuration
  ui: {
    theme: {
      primaryColor: process.env.REACT_APP_THEME_PRIMARY_COLOR || '#0066CC',
      successColor: process.env.REACT_APP_THEME_SUCCESS_COLOR || '#28A745',
      warningColor: process.env.REACT_APP_THEME_WARNING_COLOR || '#FFC107',
      errorColor: process.env.REACT_APP_THEME_ERROR_COLOR || '#DC3545',
      infoColor: process.env.REACT_APP_THEME_INFO_COLOR || '#17A2B8',
    },
    layout: {
      sidebarWidth: 280,
      headerHeight: 80,
      mobileBreakpoint: 768,
    },
  },

  // Security Configuration - Environment Based
  security: {
    tokenStorageKey: process.env.REACT_APP_TOKEN_STORAGE_KEY || 'dashboard_token',
    sessionTimeout: parseInt(process.env.REACT_APP_SESSION_TIMEOUT, 10) || (isDev ? 86400000 : 3600000),
    refreshInterval: parseInt(process.env.REACT_APP_REFRESH_INTERVAL, 10) || (isDev ? 30000 : 60000),
    maxRetries: parseInt(process.env.REACT_APP_MAX_RETRIES, 10) || (isDev ? 3 : 5),
  },

  // Development Configuration - Environment Based
  development: {
    debugMode: process.env.REACT_APP_DEBUG_MODE === 'true' || isDev,
    logLevel: process.env.REACT_APP_LOG_LEVEL || (isDev ? 'debug' : 'info'),
    mockApi: process.env.REACT_APP_MOCK_API === 'true',
    enableDevTools: process.env.REACT_APP_ENABLE_DEV_TOOLS === 'true' || isDev,
  },

  // Plan catalog is DB-only via GET /api/v1/subscriptions/plans — do not hardcode tiers here.

  // Error Messages
  messages: {
    errors: {
      networkError: 'Network connection lost. Please check your internet connection.',
      authError: 'Authentication failed. Please restart the application.',
      paymentError: 'Payment processing failed. Please try again.',
      genericError: 'An unexpected error occurred. Please try again.',
    },
    success: {
      paymentSuccess: 'Payment processed successfully!',
      subscriptionUpdated: 'Subscription updated successfully!',
      planChanged: 'Plan changed successfully!',
    },
    info: {
      loading: 'Loading your dashboard...',
      processing: 'Processing...',
      saving: 'Saving changes...',
    }
  }
};
// ***** Code End BH29072026_1105

// Helper function to get configuration value with fallback
export const getConfig = (path, fallback = null) => {
  const keys = path.split('.');
  let value = config;
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return fallback;
    }
  }
  
  return value;
};

/** Shortcut: getConfig('api.endpoints.raw.USER_PROFILE') etc. */
export const getRawEndpoint = (name, fallback = null) =>
  getConfig(`api.endpoints.raw.${name}`, fallback);

export const isMockPaymentsAllowed = () =>
  process.env.NODE_ENV === 'development' || getConfig('features.allowMockPayments', false) === true;

// Helper function to check if feature is enabled
export const isFeatureEnabled = (featureName) => {
  return getConfig(`features.${featureName}`, false);
};

// Helper function to get API endpoint
export const getApiEndpoint = (endpointName) => {
  const baseUrl = getConfig('api.baseUrl');
  const endpoint = getConfig(`api.endpoints.${endpointName}`);
  return endpoint ? `${baseUrl}${endpoint}` : null;
};

// Helper function to get full API URL
export const getApiUrl = (endpointName, path = '') => {
  const baseEndpoint = getApiEndpoint(endpointName);
  return baseEndpoint ? `${baseEndpoint}${path}` : null;
};

// Environment detection helpers (DEV / QA / PROD)
export const isDevelopment = () => isDev;
export const isQaEnvironment = () => isQa;
export const isProduction = () => isProd;
export const getEnvironment = () => process.env.REACT_APP_ENVIRONMENT || appEnvironment;

export default config;
